import { useEffect, useState, createContext, useContext, useMemo } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_TO_VAR = {
  color_accent: "--theme-accent",
  color_accent_hover: "--theme-accent-hover",
  color_bg: "--theme-bg",
  color_bg_card: "--theme-bg-card",
  color_bg_elevated: "--theme-bg-elevated",
  color_text: "--theme-text",
  color_text_muted: "--theme-text-muted",
  color_border: "--theme-border",
  font_family_heading: "--theme-font-heading",
  font_family_body: "--theme-font-body",
  font_family_mono: "--theme-font-mono",
  button_radius: "--theme-button-radius",
  hero_overlay_opacity: "--theme-hero-overlay-opacity",
  hero_overlay_color: "--theme-hero-overlay-color"
};

// Defaults mirror backend DEFAULT_THEME copy fields. Used as fallback before
// /api/theme resolves and when a field has not been customized yet.
export const DEFAULT_TEXTS = {
  site_html_title: "TaxiNearMe | Venice luxury Transfer",
  hero_h1_line1: "Premium Transfers",
  hero_h1_line2: "From Venice",
  hero_subtitle: "Luxury chauffeured transfers from Venice to the Dolomites, Italian Lakes, Tuscany, and 50+ destinations across Italy and beyond.",
  hero_check_1: "Fixed Rates",
  hero_check_2: "Free Cancellation 24h",
  hero_check_3: "Professional Chauffeurs",
  prefooter_title: "Travel in style with our Mercedes-Benz fleet",
  prefooter_subtitle: "Hand-picked vehicles, professional chauffeurs, and an obsession with detail.",
  footer_about: "Premium transfer services from Venice across Italy and beyond.",
  footer_contact_phone: "+39 345 678 9012",
  footer_contact_email: "info@taxinearme.it",
  footer_copyright: "\u00a9 2026 TaxiNearMe. All rights reserved.",
  logo_text: "TaxiNearMe",
  // Fleet section
  fleet_section_title: "Travel in style with our Mercedes-Benz fleet",
  fleet_section_subtitle: "Hand-picked vehicles, professional chauffeurs, and an obsession with detail.",
  fleet_v1_name: "Mercedes V-Class",
  fleet_v1_desc: "Premium comfort for groups up to 7 passengers",
  fleet_v1_pax: "7",
  fleet_v1_luggage: "6",
  fleet_v1_rate: "Standard Rate",
  fleet_v2_name: "Mercedes S-Class",
  fleet_v2_desc: "Ultimate luxury sedan for executive travel",
  fleet_v2_pax: "3",
  fleet_v2_luggage: "3",
  fleet_v2_rate: "+25%",
  fleet_v3_name: "Mercedes Sprinter",
  fleet_v3_desc: "Spacious minibus for larger groups",
  fleet_v3_pax: "12",
  fleet_v3_luggage: "12",
  fleet_v3_rate: "By Quote",
  // Booking CTA
  booking_cta_primary: "Get your quote",
  booking_cta_subtitle: "we'll get back to you in a few minutes",
  // Hero gallery (newline-separated URLs)
  hero_background_gallery: ""
};

export const ThemeTextContext = createContext(DEFAULT_TEXTS);
export const useThemeTexts = () => useContext(ThemeTextContext);

export const applyTheme = (theme) => {
  if (!theme) return;
  const root = document.documentElement;
  const preset = theme.preset || {};
  Object.entries(PRESET_TO_VAR).forEach(([key, varName]) => {
    if (preset[key]) root.style.setProperty(varName, preset[key]);
  });

  // Update logo text via data attr (consumed by CSS or JS)
  if (preset.logo_text) {
    root.setAttribute("data-theme-logo", preset.logo_text);
  }

  // Update HTML <title>
  if (preset.site_html_title && typeof document !== "undefined") {
    document.title = preset.site_html_title;
  }

  // Update favicon (browser tab icon). Accepts absolute URLs (https://...) and
  // backend-relative paths (/api/files/...). Falls back to the static one when empty.
  if (typeof document !== "undefined") {
    const FAVICON_DEFAULT = "/favicon.ico";
    let href = (preset.favicon_url || "").trim();
    if (href && href.startsWith("/api/")) {
      href = `${BACKEND_URL}${href}`;
    }
    if (!href) href = FAVICON_DEFAULT;
    // Bust cache only when value has actually changed
    let link = document.querySelector('link[rel~="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== href) {
      link.setAttribute("href", href);
    }
  }

  // Inject custom CSS
  let styleTag = document.getElementById("admin-custom-css");
  if (!styleTag) {
    styleTag = document.createElement("style");
    styleTag.id = "admin-custom-css";
    document.head.appendChild(styleTag);
  }
  styleTag.textContent = theme.custom_css || "";
};

export default function ThemeProvider({ children }) {
  const [preset, setPreset] = useState({});

  useEffect(() => {
    let active = true;
    axios.get(`${API}/theme`)
      .then(res => {
        if (!active) return;
        applyTheme(res.data);
        setPreset(res.data?.preset || {});
      })
      .catch(() => { /* fall back to default css */ });

    const handler = (e) => {
      applyTheme(e.detail);
      setPreset(e.detail?.preset || {});
    };
    window.addEventListener("theme:update", handler);
    return () => { active = false; window.removeEventListener("theme:update", handler); };
  }, []);

  const texts = useMemo(() => ({ ...DEFAULT_TEXTS, ...preset }), [preset]);

  return <ThemeTextContext.Provider value={texts}>{children}</ThemeTextContext.Provider>;
}
